import { useMemo } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useRevenueSyncStore } from '../store/revenueSyncStore.js'
import { TEAMS } from '../data/teams.js'
import { tldrWinsFlagsDiffer, hsTotalsDiffer } from '../data/summarySnapshot.js'

/**
 * useSummaryStaleness — detects whether the current exec summary is
 * stale relative to the data that feeds into it.
 *
 * Two-tier detection:
 *
 *   1. SNAPSHOT MODE (preferred).
 *      When the saved exec summary has `generatedInputs` (a frozen
 *      snapshot of what the AI saw at generation time), we diff the
 *      current state against that snapshot. This is content-based,
 *      not timestamp-based — adding text then deleting it is correctly
 *      treated as "no net change," and HubSpot syncs that don't move
 *      the dollar totals don't trigger a stale notification.
 *
 *   2. LEGACY MODE (fallback).
 *      For exec summaries saved before the snapshot feature shipped,
 *      we fall back to timestamp comparison — but with guards so
 *      cleared-to-empty fields don't flag. Imperfect, but covers the
 *      backwards-compat case until the next regeneration captures a
 *      proper snapshot.
 *
 * Returns:
 *   isStale:    boolean
 *   staleCount: number of changed inputs
 *   changes:    [{ type, label, author, at, detail? }] sorted newest-first
 *   generatedAt: ISO string | null
 */
export function useSummaryStaleness() {
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const lastSyncReceipt = useRevenueSyncStore((s) => s.lastReceipt)

  return useMemo(() => {
    const execSummary = meta?.execSummary
    const generatedAt = execSummary?.generatedAt || null
    if (!generatedAt) {
      return { isStale: false, staleCount: 0, changes: [], generatedAt: null }
    }
    const generatedMs = new Date(generatedAt).getTime()
    if (!Number.isFinite(generatedMs)) {
      return { isStale: false, staleCount: 0, changes: [], generatedAt }
    }

    const snapshot = execSummary.generatedInputs
    const changes = snapshot
      ? diffAgainstSnapshot(meta, goalsData, lastSyncReceipt, snapshot, generatedMs)
      : diffByTimestamps(meta, goalsData, lastSyncReceipt, generatedMs)

    changes.sort((a, b) => {
      const ta = new Date(a.at).getTime() || 0
      const tb = new Date(b.at).getTime() || 0
      return tb - ta
    })

    return {
      isStale: changes.length > 0,
      staleCount: changes.length,
      changes,
      generatedAt,
    }
  }, [meta, goalsData, lastSyncReceipt])
}

/* ── Mode 1: snapshot diff ────────────────────────────────────────── */

function diffAgainstSnapshot(meta, goalsData, lastSyncReceipt, snapshot, generatedMs) {
  const changes = []

  // Weekly summaries: both directions
  const currentWS = meta?.weeklySummary || {}
  const snapWS = snapshot.weeklySummary || {}
  const wsKeys = new Set([...Object.keys(currentWS), ...Object.keys(snapWS)])
  for (const key of wsKeys) {
    const cur = currentWS[key] || {}
    const snap = snapWS[key]
    const curHasContent = cur.tldr || cur.wins || cur.flags
    const snapHasContent = !!snap // snapshot only stores entries that had content
    if (!curHasContent && !snapHasContent) continue
    if (curHasContent && snapHasContent && !tldrWinsFlagsDiffer(cur, snap)) continue
    const label = resolveGoalLabel(goalsData, key) || key
    const at = cur.savedAt || new Date(generatedMs).toISOString()
    changes.push({
      type: 'Goal',
      label,
      author: cur.savedBy || '',
      at,
      detail: !curHasContent && snapHasContent ? 'content removed' : undefined,
    })
  }

  // Team updates
  const currentTU = meta?.teamUpdates || {}
  const snapTU = snapshot.teamUpdates || {}
  const tuKeys = new Set([...Object.keys(currentTU), ...Object.keys(snapTU)])
  for (const id of tuKeys) {
    const cur = currentTU[id] || {}
    const snap = snapTU[id]
    const curHasContent = cur.tldr || cur.wins || cur.flags
    const snapHasContent = !!snap
    if (!curHasContent && !snapHasContent) continue
    if (curHasContent && snapHasContent && !tldrWinsFlagsDiffer(cur, snap)) continue
    const team = TEAMS.find((t) => t.id === id)
    changes.push({
      type: 'Team',
      label: team ? `${team.name} team` : id,
      author: cur.savedBy || cur.dri || '',
      at: cur.savedAt || new Date(generatedMs).toISOString(),
      detail: !curHasContent && snapHasContent ? 'content removed' : undefined,
    })
  }

  // Action items: compare IDs of currently-open items against the
  // snapshot's open list. Added items → change. Removed items → change.
  // Status toggled from done to open or vice versa → change (flips the
  // set membership).
  const currentOpenIds = new Set()
  const goalKeyOf = {} // itemId → goalKey so we can attribute labels later
  const itemMeta = {}  // itemId → {task, createdAt, createdBy}
  for (const [gKey, items] of Object.entries(meta?.actionItems || {})) {
    if (!Array.isArray(items)) continue
    for (const item of items) {
      if (!item?.id) continue
      if (item.status === 'done') continue
      currentOpenIds.add(item.id)
      goalKeyOf[item.id] = gKey
      itemMeta[item.id] = item
    }
  }
  const snapOpenIds = new Set(snapshot.actionItemIds || [])
  // Items open now but not in the snapshot = added/reopened
  for (const id of currentOpenIds) {
    if (snapOpenIds.has(id)) continue
    const item = itemMeta[id]
    const gKey = goalKeyOf[id]
    changes.push({
      type: 'Action',
      label: resolveGoalLabel(goalsData, gKey) || gKey,
      author: item?.createdBy || '',
      at: item?.createdAt || new Date(generatedMs).toISOString(),
      detail: item?.task || '',
    })
  }
  // Items in the snapshot but no longer open = completed/removed since
  const removedSince = [...snapOpenIds].filter((id) => !currentOpenIds.has(id))
  if (removedSince.length > 0) {
    changes.push({
      type: 'Action',
      label: `${removedSince.length} follow-up${removedSince.length === 1 ? '' : 's'} closed or removed`,
      author: '',
      at: new Date().toISOString(),
    })
  }

  // Sub-goal update text
  const currentSG = {}
  if (Array.isArray(goalsData)) {
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      if (!pillar?.goals) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const goal = pillar.goals[gi]
        if (!goal || goal.type === 'section') continue
        const subs = goal.subgoals || goal.subGoals || []
        for (let si = 0; si < subs.length; si++) {
          const update = subs[si]?.update
          if (update && String(update).trim()) {
            currentSG[`p${pi}-g${gi}-s${si}`] = String(update)
          }
        }
      }
    }
  }
  const snapSG = snapshot.subgoalUpdates || {}
  const sgKeys = new Set([...Object.keys(currentSG), ...Object.keys(snapSG)])
  for (const key of sgKeys) {
    const cur = currentSG[key]
    const snap = snapSG[key]
    if (cur === snap) continue
    if (!cur && !snap) continue
    const m = /^p(\d+)-g(\d+)-s(\d+)$/.exec(key)
    let label = key
    let author = ''
    let at = new Date(generatedMs).toISOString()
    if (m) {
      label = resolveSubgoalLabel(goalsData, Number(m[1]), Number(m[2]), Number(m[3])) || key
      // Best-effort attribution from fieldEdits
      const edit = meta?.fieldEdits?.[`${key}-update`]
      if (edit) {
        author = edit.by || ''
        at = edit.at || at
      }
    }
    changes.push({
      type: 'Subgoal',
      label,
      author,
      at,
      detail: !cur && snap ? 'content removed' : undefined,
    })
  }

  // HubSpot: emit a change only when totals actually differ.
  if (lastSyncReceipt?.totals && hsTotalsDiffer(lastSyncReceipt.totals, snapshot.hsTotals)) {
    changes.push({
      type: 'HubSpot',
      label: 'Revenue numbers changed since last sync',
      author: '',
      at: lastSyncReceipt.updatedAt || new Date().toISOString(),
      detail: `total $${Math.round(lastSyncReceipt.totals.totalHsRev).toLocaleString()}`,
    })
  }

  return changes
}

/* ── Mode 2: legacy timestamp diff (no snapshot available) ──────── */

function diffByTimestamps(meta, goalsData, lastSyncReceipt, generatedMs) {
  const changes = []

  // Only flag entries that actually have content now — prevents the
  // "wrote then deleted" pseudo-staleness bug. (Doesn't catch the
  // reverse — deleted pre-existing content — but that's rarer and
  // a regenerate will reset the baseline.)
  for (const [key, entry] of Object.entries(meta?.weeklySummary || {})) {
    const savedMs = entry?.savedAt ? new Date(entry.savedAt).getTime() : 0
    if (!Number.isFinite(savedMs) || savedMs <= generatedMs) continue
    if (!entry.tldr && !entry.wins && !entry.flags) continue
    changes.push({
      type: 'Goal',
      label: resolveGoalLabel(goalsData, key) || key,
      author: entry.savedBy || '',
      at: entry.savedAt,
    })
  }

  for (const [teamId, entry] of Object.entries(meta?.teamUpdates || {})) {
    const savedMs = entry?.savedAt ? new Date(entry.savedAt).getTime() : 0
    if (!Number.isFinite(savedMs) || savedMs <= generatedMs) continue
    if (!entry.tldr && !entry.wins && !entry.flags) continue
    const team = TEAMS.find((t) => t.id === teamId)
    changes.push({
      type: 'Team',
      label: team ? `${team.name} team` : teamId,
      author: entry.savedBy || entry.dri || '',
      at: entry.savedAt,
    })
  }

  for (const [key, items] of Object.entries(meta?.actionItems || {})) {
    if (!Array.isArray(items)) continue
    for (const item of items) {
      const createdMs = item?.createdAt ? new Date(item.createdAt).getTime() : 0
      if (!Number.isFinite(createdMs) || createdMs <= generatedMs) continue
      changes.push({
        type: 'Action',
        label: resolveGoalLabel(goalsData, key) || key,
        author: item.createdBy || '',
        at: item.createdAt,
        detail: item.task || '',
      })
    }
  }

  for (const [key, edit] of Object.entries(meta?.fieldEdits || {})) {
    const m = /^p(\d+)-g(\d+)-s(\d+)-update$/.exec(key)
    if (!m) continue
    const atMs = edit?.at ? new Date(edit.at).getTime() : 0
    if (!Number.isFinite(atMs) || atMs <= generatedMs) continue
    changes.push({
      type: 'Subgoal',
      label: resolveSubgoalLabel(goalsData, Number(m[1]), Number(m[2]), Number(m[3])) || key,
      author: edit.by || '',
      at: edit.at,
    })
  }

  // Legacy HS handling: intentionally silent.
  //
  // We have no snapshot of what the HubSpot totals were at generation
  // time, so we can't tell whether a post-generation sync actually
  // changed the dollar amounts or just re-pulled the same numbers. An
  // automatic 10-minute sync that finds no new deals would otherwise
  // fire a false-positive "Revenue sync refreshed" chip forever.
  //
  // Once the user regenerates once, the new snapshot captures hsTotals
  // and the snapshot-mode detector gives us accurate HS change detection.
  // Until then, HS changes aren't reported — a small loss that's
  // preferable to persistent noise.

  return changes
}

/* ── Helpers ─────────────────────────────────────────────────────── */

function resolveGoalLabel(goalsData, key) {
  const m = /^p(\d+)-g(\d+)$/.exec(key)
  if (!m || !Array.isArray(goalsData)) return null
  const pi = Number(m[1])
  const gi = Number(m[2])
  const pillar = goalsData[pi]
  const goal = pillar?.goals?.[gi]
  if (!pillar || !goal) return null
  return `${pillar.pillar} › ${goal.goal || goal.name || 'Untitled goal'}`
}

function resolveSubgoalLabel(goalsData, pi, gi, si) {
  if (!Array.isArray(goalsData)) return null
  const pillar = goalsData[pi]
  const goal = pillar?.goals?.[gi]
  const sub = goal?.subgoals?.[si] || goal?.subGoals?.[si]
  if (!pillar || !goal || !sub) return null
  return `${pillar.pillar} › ${goal.goal || goal.name || 'Untitled'} › ${sub.subgoal || 'Untitled'}`
}
